import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Space } from '@client/components/_ui/layout/Space';
import {
    createCommonFields,
    createFilterConfig,
    type Filter,
    FilterBar,
    FilterSchema,
    isFilterValueEmpty,
    isWhereClauseEmpty,
    useFilterBuilder,
} from '@client/components/filters';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, openRowOnClick, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/product-types/$productTypeId')({
    staticData: {
        metadata: {
            title: 'PDS configuration',
        },
        // The page title names the product type (`beforeLoad`): a static `ui.title` would win over it.
        ui: {
            subtitle: 'Manage Product Disclosure Statements for this product type',
        },
        presentation: 'fullPage',
    },
    params: {
        parse({ productTypeId }) {
            return {
                productTypeId: BigInt(productTypeId),
            };
        },
    },
    beforeLoad({ context: { trpcUtils }, params }): ShareableRouteContext {
        const productType = () => trpcUtils.productType.getProductType.ensureData({ id: params.productTypeId });
        void productType().catch(() => undefined);

        return {
            breadcrumb: {
                title: async () => (await productType()).name,
            },
            // Route context flows down to every descendant match: the PDS versions page sets its own
            // static title, and the decision engine displaces this one in its own `beforeLoad`.
            // A failed read keeps the page's own name rather than the layout's "-".
            ui: {
                title: async () =>
                    productType().then(
                        ({ name }) => `PDS documents for ${name}`,
                        () => 'PDS configuration',
                    ),
            },
        };
    },
    component: ConfigurationPdsByProductType,
});

type PdsWhere = NonNullable<RouterInputs['pds']['listPds']['where']>;

// Create typed commonFields for PdsWhere
const commonFields = createCommonFields<PdsWhere>();

/** The largest Postgres `bigint`, the type of a PDS ID. */
const INT8_MAX = 9_223_372_036_854_775_807n;

/**
 * The filter bar for the PDS list. The page owns the filter model: it keeps it in the table's URL
 * state with the `where` built from it, so a shared link restores the search box and the rows
 * together. Mount it again (a new `key`) to show a different `initialFilters`.
 */
function PdsFilters({
    initialFilters,
    onChange,
}: {
    initialFilters: Filter[];
    /** A user change: the Prisma where clause and the filters it was built from. */
    onChange: (where: PdsWhere, filters: Filter[]) => void;
}) {
    // Create filter configuration
    const filterConfig = useMemo(() => {
        return createFilterConfig<PdsWhere>([
            // Pinned text search field for searching by name
            commonFields.textSearch({
                id: 'search',
                pinned: true,
                operators: ['contains'],
                defaultOperator: 'contains',
                placeholder: 'Search by name',
                customWhereBuilder(operator, value) {
                    return match(operator)
                        .with('contains', () => {
                            // Try to parse as ID first. A number past Postgres `bigint` would fail the whole
                            // query, so it searches by name only.
                            const idMatch = value.match(/^\d+$/);
                            const parsed = idMatch ? BigInt(value) : BigInt(0);
                            const id = parsed <= INT8_MAX ? parsed : BigInt(0);

                            return {
                                OR: [{ id: { equals: id } }, { name: { contains: value, mode: 'insensitive' } }],
                            } satisfies PdsWhere;
                        })
                        .otherwise(() => null);
                },
            }),

            // ID filter
            commonFields.id({
                id: 'pds-id',
                label: 'ID',
                dbField: 'id',
            }),
        ]);
    }, []);

    // The debounced callback runs after the render holding the filters it built `where` from.
    const latestFilters = useRef(initialFilters);
    // The mount-time replay (`interacted` false) is skipped: `initialFilters` came with its `where`.
    const filterState = useFilterBuilder(filterConfig, initialFilters, (where, _searchTerms, interacted) => {
        if (interacted) onChange(where, latestFilters.current);
    });
    latestFilters.current = filterState.filters;

    return (
        <FilterBar
            config={filterConfig}
            filters={filterState.filters}
            whereClause={filterState.whereClause}
            onAddFilter={filterState.addFilter}
            onUpdateFilter={filterState.updateFilter}
            onRemoveFilter={filterState.removeFilter}
            onClearFilters={filterState.clearFilters}
        />
    );
}

type QueryParams = RouterInputs['pds']['listPds'];
type PdsRow = RouterOutputs['pds']['listPds']['rows'][number];
type PdsOrderBy = NonNullable<QueryParams['orderBy']>;
/** The table's URL state: the query, plus the filter bar's model its `where` was built from. */
type PdsView = QueryParams & { filters?: Filter[] };

const FiltersSchema = z.array(FilterSchema);

/** The filter model carried by parameters handed back by the table, or restored from the URL. */
function parseFilters(value: unknown): Filter[] | undefined {
    const parsed = FiltersSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}

/** Leaves out an empty filter model and `where`, so clearing the filters returns the URL to the defaults. */
function withFilters(view: PdsView, where: QueryParams['where'], filters: Filter[]): PdsView {
    const { where: _where, filters: _filters, ...rest } = view;
    const meaningful = filters.filter((filter) => !isFilterValueEmpty(filter.operator, filter.value));
    return {
        ...rest,
        ...(where && !isWhereClauseEmpty(where) ? { where } : {}),
        ...(meaningful.length ? { filters: meaningful } : {}),
    };
}

function ConfigurationPdsByProductType() {
    const orgId = useOrgId();
    const { productTypeId } = Route.useParams();
    const navigate = useNavigate();

    const [view, setView] = useState<PdsView>({
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        orderBy: { id: 'desc' },
    });
    // Bumped when the filter model changes from outside the filter bar (restored from the URL, or reset
    // to the defaults), so the bar mounts again showing it.
    const [filterBarKey, setFilterBarKey] = useState(0);
    const { filters, ...input } = view;

    const { data, isPending, error, isFetching, refetch } = trpc.pds.listPds.useQuery(
        {
            ...input,
            where: {
                ...input.where,
                productTypeId,
            },
        },
        {
            enabled: !!productTypeId,
        },
    );

    const columnHelper = createColumnHelper<PdsRow, PdsOrderBy>();

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ row }) => row.id.toString(),
            width: 80,
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
            cell: ({ row }) => <span className="font-medium">{row.name}</span>,
            hideable: false,
        }),
        columnHelper.display({
            id: 'insurer',
            header: 'Insurer',
            cell: ({ row }) => row.Insurer?.name ?? <span className="text-subtle-foreground">-</span>,
            width: 280,
        }),
        columnHelper.actions({
            // The row opens its editor on click; Edit is the keyboard path to the same drawer.
            cell: ({ row }) => (
                <Space size="small">
                    <Button.Edit.Link
                        to="/console/$orgId/configuration/claims-products/product-types/$productTypeId/edit/$pdsId"
                        params={{ orgId, productTypeId, pdsId: row.id }}
                        aria-label={`Edit ${row.name}`}
                    />
                    <Button.Link
                        to="/console/$orgId/configuration/claims-products/product-types/$productTypeId/pds/$pdsId"
                        params={{ orgId, productTypeId: row.productTypeId, pdsId: row.id }}
                        variant="tertiary"
                    >
                        PDS versions
                    </Button.Link>
                    <Button.Link
                        to="/console/$orgId/configuration/claims-products/product-types/$productTypeId/pds-text/$pdsId"
                        params={{ orgId, productTypeId, pdsId: row.id }}
                        variant="tertiary"
                        icon={<FileText size={14} />}
                    >
                        PDS text
                    </Button.Link>
                </Space>
            ),
            width: 320,
        }),
    ];

    const tableProps = usePrismaTable({
        data: data?.rows ?? [],
        total: data?.total ?? 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: view,
        onParamsChange: (params) => {
            // Sort and page changes carry the current model along unchanged.
            const carried = 'filters' in params ? params.filters : undefined;
            if (carried === filters) {
                setView(params);
                return;
            }
            const { filters: _filters, ...rest } = { ...params, filters: undefined };
            const next = parseFilters(carried);
            setView(next ? { ...rest, filters: next } : rest);
            setFilterBarKey((key) => key + 1);
        },
        // Search, filters, sort and page survive a refresh and a shared link.
        urlKey: 'q',
        queryError: error,
        emptyState: { noRecordsTitle: 'No PDS documents for this product type yet' },
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'pds-configuration-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <PageController route={Route}>
            {/* The toolbar stays through a failed read: the search that caused it must stay there to clear. */}
            <TableToolbar
                filters={
                    <PdsFilters
                        key={filterBarKey}
                        initialFilters={filters ?? []}
                        onChange={(where, next) =>
                            setView((prev) => ({ ...withFilters(prev, where, next), offset: 0 }))
                        }
                    />
                }
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/claims-products/product-types/$productTypeId/add"
                        params={{ orgId, productTypeId }}
                        variant="primary"
                        label="New PDS"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                error={error}
                onRetry={() => void refetch()}
                retrying={isFetching}
                onRow={openRowOnClick(({ id }, e) =>
                    navigate(
                        {
                            to: '/console/$orgId/configuration/claims-products/product-types/$productTypeId/edit/$pdsId',
                            params: { orgId, productTypeId, pdsId: id },
                        },
                        e,
                    ),
                )}
            />

            {/* The editors and the versions page live here: a failed list must not unmount them. */}
            <Outlet />
        </PageController>
    );
}
